import { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import service from 'util/apiService';
import { getCurrentViewResolvedSiteAndCostCenter, getIsViewAndUserConfigReady } from 'modules/cbre-flow-view-management/ducks/selectors';
import {
  type ReturnUseApprovalsCount,
  type ApprovalsCount,
  type ReturnUseApprovalsListApiProps,
  type UseApprovalsListApiProps,
  type UseApprovalsCountProps,
} from '../types';

const useApprovalsCount = <T>({
  url: baseUrl,
  queryParams = {},
  payload = null,
  loadApiOnMount,
  broadCastName = ''
}: UseApprovalsCountProps<T>): ReturnUseApprovalsCount => {
  const [approvalsCount, setApprovalsCount] = useState<ApprovalsCount>({
    myApprovalCount: 0,
    externalApprovalCount: 0,
    historyCount: 0,
    externalApprovalsHistoryCount: 0,
    outsideViewMyApprovalCount: 0,
    outsideViewExternalApprovalCount: 0,
  });
  const { sites: viewManagedSites = [] } = useSelector(getCurrentViewResolvedSiteAndCostCenter);
  const isViewAndUserConfigReady = useSelector(getIsViewAndUserConfigReady);
  const approvalsCountBroadCast = useRef(new BroadcastChannel(`approvalsCount_${broadCastName}`)).current
  const [url, setUrl] = useState<string>(baseUrl);
  const normalizedManagedSites = useMemo(
    () => (Array.isArray(viewManagedSites)
      ? viewManagedSites
        .map((site) => (typeof site === 'string' ? site.trim() : ''))
        .filter(Boolean)
      : []),
    [viewManagedSites],
  );

  const fetchApprovalsCountBackup = async () => {
    try {
      const { data = {}} = await service.get(`${url}/approvals/counts`);
      const resp = {...approvalsCount, ...data}
      approvalsCountBroadCast.postMessage(resp);
      setApprovalsCount(resp);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }

  const fetchApprovalsCount = async () => {
    try {
      if (!isViewAndUserConfigReady) {
        return;
      }

      const requestPayload = {
        filters: normalizedManagedSites.length > 0
          ? [{ name: 'site', value: normalizedManagedSites }]
          : [],
      };

      const { data = {}} = await service.post(`${url}/approvals/counts`, requestPayload);
      const resp = {...approvalsCount, ...data}
      approvalsCountBroadCast.postMessage(resp);
      setApprovalsCount(resp);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }

  useEffect(() => {
    approvalsCountBroadCast.onmessage = ({data}: MessageEvent<ApprovalsCount>) => {
      setApprovalsCount(()=> ({...data}));
    };
  }, [approvalsCountBroadCast, setApprovalsCount]);

  useEffect(() => {
    fetchApprovalsCount()
  }, [url, isViewAndUserConfigReady, normalizedManagedSites]);

  return { ...approvalsCount, fetchApprovalsCount };
};

export default useApprovalsCount;
